<template>
  <BModal
    id="approveModal"
    centered
    hide-footer
    :title="title"
    ref="approveModal"
    @shown="onModalShown"
  >
    <BCardText class="text-center">
      <div class="mb-2">
        <template v-if="action === 'send_offer' && invoice && invoice.status === 12 && invoice.msg">
            <p><strong style="font-size: 18px; color: red;">{{invoice.msg}}</strong></p>
            <p><strong>{{$t('reminder_offer_problems')}}</strong></p>
        </template>

        <template v-if="usersWithoutAddress.length > 0">
          <div class="alert alert-warning text-left">
            <h5 class="alert-heading">{{ $t('missing_addresses_warning') }}</h5>
            <div class="p-2">
              <p>{{ $t('following_users_missing_addresses') }}:</p>
              <ul class="mb-2">
                <li v-for="user in usersWithoutAddress" :key="user.id">
                  {{ user.full_name }} (U-{{ user.id }})
                </li>
              </ul>
              <p class="mb-0">{{ $t('add_addresses_or_send_anyway') }}</p>
            </div>
          </div>
        </template>

        <strong v-if="action !== 'send_offer'">
          {{ textContent }}
        </strong>
        <div v-else>
          <BFormGroup :label="$t('acknowledgment')">
            <BFormSelect
              v-model="acknowledgmentLocal"
              :options="acknowledgmentTypeOptions"
              class="custom-border"
            />
          </BFormGroup>
          <BFormGroup v-if="!isNonRecourseInvoice" class="mt-1">
            <Multiselect
                v-model="multiselectVal"
                :options="options"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :placeholder="$t('select_recipients')"
                label="text"
                track-by="value"
                :class="{ 'is-invalid': showValidationError }"
            />
            <div v-if="showValidationError" class="invalid-feedback d-block">
              {{ $t('please_select_at_least_one_user') }}
            </div>
          </BFormGroup>
        </div>
      </div>
    </BCardText>
    <hr/>
    <BRow>
      <BCol v-if="typeof showInput != 'undefined'" cols="12">
        <BFormGroup id="comment-group" :label="$t('comment') + ':'" label-for="comment">
          <BFormTextarea id="comment" v-model="showInput" required/>
        </BFormGroup>
      </BCol>
      <BCol v-if="action === 'remove' || action === 'reject'" cols="12">
        <BFormGroup id="reason-group" :label="$t('comment') + ':'" label-for="comment">
          <BFormTextarea id="reason" v-model="reason" required/>
        </BFormGroup>
      </BCol>
      <BCol class="text-center">
        <template v-if="usersWithoutAddress.length > 0">
          <BButton variant="secondary" size="sm" @click="$bvModal.hide('approveModal')" class="mr-2">
            {{ $t('cancel') }}
          </BButton>
          <BButton variant="warning" size="sm" @click="sendAnyway()">
            {{ $t('send_anyway') }}
          </BButton>
        </template>
        <template v-else>
          <BButton variant="danger" size="sm" @click="$bvModal.hide('approveModal')" class="mr-2">
            <FeatherIcon icon="XIcon"/>
          </BButton>
          <BButton variant="primary" size="sm" @click="approve()">
            <FeatherIcon icon="CheckIcon"/>
          </BButton>
        </template>
      </BCol>
    </BRow>
  </BModal>
</template>

<script>
import { BModal, BButton, BCardText, BRow, BCol, BFormGroup, BFormTextarea, BFormSelect } from 'bootstrap-vue'
import Multiselect from 'vue-multiselect'
import Ripple from 'vue-ripple-directive'

export default {
  name: "ApproveModal",
  components: {
    BButton,
    BModal,
    BCardText,
    BRow,
    BCol,
    BFormGroup,
    BFormTextarea,
    BFormSelect,
    Multiselect,
  },
  data() {
    return {
      reason: '',
      acknowledgmentTypes: [],
      acknowledgmentLocal: null,
      multiselectVal: [],
      options: [],
      showValidationError: false,
      usersWithoutAddress: [],
      usersLoadedForInvoiceId: null,
    }
  },
  directives: {
    Ripple,
  },
  props: ['textContent', 'title', 'showInput', 'action', 'invoice'],
  computed: {
    isNonRecourseInvoice() {
      if (!this.invoice) return false
      if (Number(this.invoice.is_reverse) === 1) return false
      if (Number(this.invoice.recourse) === 1) return false
      return true
    },
    acknowledgmentTypeOptions() {
      return this.acknowledgmentTypes.map(item => ({
        value: item.id,
        text: this.$t(item.name),
      }))
    },
    selectedAcknowledgmentType() {
      if (this.acknowledgmentLocal != null) {
        return this.acknowledgmentLocal
      }
      return this.invoice?.acknowledgment_type?.id ?? this.acknowledgmentTypes?.[0]?.id ?? null
    },
  },
  watch: {
    invoice: {
      immediate: true,
      handler(invoice) {
        if (invoice?.acknowledgment_type?.id) {
          this.acknowledgmentLocal = invoice.acknowledgment_type.id
        }
        this.loadCompanyUsers()
      },
    },
  },
  created() {
    this.loadAcknowledgmentTypes()
  },
  methods: {
    onModalShown() {
      this.usersWithoutAddress = []
      this.showValidationError = false
      if (this.action === 'send_offer') {
        this.loadAcknowledgmentTypes()
        this.loadCompanyUsers()
      }
    },
    loadAcknowledgmentTypes() {
      this.$http.post('/invoice/admin/get-acknowledgment-types').then((res) => {
        this.acknowledgmentTypes = res.data.value || []
        if (this.acknowledgmentLocal == null && this.acknowledgmentTypes.length) {
          this.acknowledgmentLocal = this.invoice?.acknowledgment_type?.id ?? this.acknowledgmentTypes[0].id
        }
      })
    },
    loadCompanyUsers() {
      if (!this.invoice?.id || this.usersLoadedForInvoiceId === this.invoice.id) {
        return
      }
      this.usersLoadedForInvoiceId = this.invoice.id
      this.options = []
      this.multiselectVal = []
      this.$http.get(`company/admin/${this.invoice.id}/users`).then((res) => {
        const { all_users: allUsers = [], selected_users: selectedUsers = [] } = res.data.value || {}
        this.options = allUsers.map(element => ({
          text: element.full_name,
          value: element.id,
        }))
        this.multiselectVal = selectedUsers.map(element => ({
          text: element.full_name,
          value: element.id,
        }))
      })
    },
    requiresRecipients() {
      return this.action === 'send_offer' && !this.isNonRecourseInvoice
    },
    async checkUsersWithoutAddress(userIds) {
      if (!userIds || userIds.length === 0) {
        this.usersWithoutAddress = []
        return true
      }

      try {
        const response = await this.$http.post('/company/admin/check-user-addresses', {
          user_ids: userIds,
        })
        const usersWithoutAddress = response?.data?.value?.users_without_address || []
        this.usersWithoutAddress = usersWithoutAddress
        return usersWithoutAddress.length === 0
      } catch (error) {
        console.error('Error checking user addresses:', error)
        this.usersWithoutAddress = []
        return true
      }
    },
    async approve() {
      if (this.action === 'send_offer') {
        if (this.selectedAcknowledgmentType == null) {
          return
        }
        if (this.requiresRecipients()) {
          if (!this.multiselectVal || this.multiselectVal.length === 0) {
            this.showValidationError = true
            return
          }
          const hasAllAddresses = await this.checkUsersWithoutAddress(this.parseMultiselectResponse())
          if (!hasAllAddresses) return
          this.showValidationError = false
        }
      }
      this.proceedWithApproval()
    },

    sendAnyway() {
      this.usersWithoutAddress = []
      this.proceedWithApproval()
    },

    proceedWithApproval() {
      this.$parent.showInput = this.showInput
      this.$parent.approve = true
      this.$parent.reason = this.reason
      this.$parent.callApproveModalAction(
        this.selectedAcknowledgmentType,
        this.isNonRecourseInvoice ? null : this.parseMultiselectResponse(),
      )
      this.$refs.approveModal.hide()
    },

    parseMultiselectResponse() {
      return this.multiselectVal?.map(item => item.value)
    },
  },
}
</script>


<style scoped>
.custom-border {
    border: 1px solid #e8e8e8;
    border-radius: 0.357rem;
}

.is-invalid >>> .multiselect__tags {
    border-color: #dc3545;
}

.invalid-feedback {
    color: #dc3545;
    font-size: 0.857rem;
    margin-top: 0.25rem;
}
</style>
